import React from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";

const FeatureCard = ({ icon, title, description, to, cta }) => (
  <Link
    to={to}
    className="group flex flex-col gap-3 p-6 rounded-xl border border-theme-secondary bg-theme-secondary hover:border-theme-primary transition-colors"
  >
    <div className="w-10 h-10 rounded-lg bg-theme-main flex items-center justify-center text-theme-primary">
      {icon}
    </div>
    <div>
      <h3 className="font-semibold text-theme-text mb-1">{title}</h3>
      <p className="text-sm text-theme-text opacity-60 leading-relaxed">
        {description}
      </p>
    </div>
    <span className="mt-auto text-sm font-medium text-theme-primary opacity-80 group-hover:opacity-100 transition-opacity">
      {cta} →
    </span>
  </Link>
);

const StatPill = ({ label }) => (
  <span className="px-3 py-1 rounded-full text-xs font-medium bg-theme-secondary border border-theme-secondary text-theme-text opacity-70">
    {label}
  </span>
);

const Home = ({ currentUser }) => {
  return (
    <DashboardLayout title="Welcome to MoonScroll" currentUser={currentUser}>
      <div className="max-w-4xl">
        {/* Hero */}
        <div className="mb-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-theme-text leading-tight mb-4">
            Trends don't sleep. Neither does MoonScroll.
          </h2>
          <p className="text-base text-theme-text opacity-60 max-w-xl leading-relaxed">
            Add a brand and MoonScroll analyzes it, finds the audiences that
            matter, surfaces what works in them, and helps you generate ideas
            and scripts grounded in real performance patterns.
          </p>
          {/* <div className="flex flex-wrap gap-2 mt-5">
            <StatPill label="Trends don't sleep. Neither does MoonScroll." />
            <StatPill label="Powered by AI" />
          </div> */}
        </div>

        {/* Feature cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          <FeatureCard
            to="/brand/"
            cta="Add a brand"
            title="Brand"
            description="Add your brand and we analyze your voice, content, and positioning to tailor everything that follows."
            icon={
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.8}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z"
                />
              </svg>
            }
          />
          <FeatureCard
            to="/research/"
            cta="Explore research"
            title="Research"
            description="Discover the audiences around your brand and see which creators and videos are actually driving attention."
            icon={
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.8}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0-1 3m8.5-3 1 3m0 0 .5 1.5m-.5-1.5h-9.5m0 0-.5 1.5m.75-9 3-3 2.148 2.148A12.061 12.061 0 0116.5 7.605"
                />
              </svg>
            }
          />
          <FeatureCard
            to="/ideas/"
            cta="View ideas"
            title="Ideas & scripts"
            description="Turn audience insights into content ideas and scripts that fit your brand and reflect what works in the wild."
            icon={
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.8}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                />
              </svg>
            }
          />
        </div>

        {/* How it works */}
        <div className="border border-theme-secondary rounded-xl p-6 sm:p-8">
          <h3 className="text-sm font-semibold text-theme-text opacity-50 uppercase tracking-widest mb-6">
            How it works
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              {
                step: "01",
                title: "Add your brand",
                body: "Connect your brand and MoonScroll analyzes your voice, content, and positioning.",
              },
              {
                step: "02",
                title: "Find your audiences",
                body: "We map the audiences around your brand and identify what's working in them.",
              },
              {
                step: "03",
                title: "Generate ideas & scripts",
                body: "Turn those patterns into content ideas and scripts tailored to your brand.",
              },
            ].map(({ step, title, body }) => (
              <div key={step} className="flex flex-col gap-2">
                <span className="text-2xl font-bold text-theme-primary opacity-30">
                  {step}
                </span>
                <h4 className="font-semibold text-theme-text">{title}</h4>
                <p className="text-sm text-theme-text opacity-55 leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Home;
